import { Client, xml } from "@xmpp/client"
import type { Participant } from "../reducers/chatsSlice"
import VCardParser from "../lib/VCardParser"

type PhotoData = {
  type?: string // MIME type (e.g., 'image/jpeg', 'image/png')
  binval?: string // BASE64 encoded image data
  extval?: string // External URL
}

const XmppVCardApi = {
  fetchVCard: async (
    client: Client,
    jid: string,
  ): Promise<Participant | undefined> => {
    const { iqCaller } = client

    const result = await iqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "get",
          to: jid,
        },
        xml("vCard", "vcard-temp"),
      ),
    )
    return VCardParser.parse(result)
  },

  updateVCardPhoto: async (
    client: Client,
    photoData: PhotoData,
  ): Promise<void> => {
    const { iqCaller } = client

    // First fetch the current vCard
    const result = await iqCaller.request(
      xml(
        "iq",
        {
          to: client.jid?.bare().toString(),
          type: "get",
        },
        xml("vCard", "vcard-temp"),
      ),
    )

    // Get the current vCard element
    let vCardElement = result.getChild("vCard", "vcard-temp")

    // If there's no vCard, create a new one
    if (!vCardElement) {
      vCardElement = xml("vCard", { xmlns: "vcard-temp" })
    }

    // Create the photo element
    let photoElement
    if (photoData.binval) {
      photoElement = xml(
        "PHOTO",
        {},
        xml("TYPE", {}, photoData.type || "image/jpeg"),
        xml("BINVAL", {}, photoData.binval),
      )
    } else if (photoData.extval) {
      photoElement = xml("PHOTO", {}, xml("EXTVAL", {}, photoData.extval))
    } else {
      throw new Error("Either binval or extval must be provided")
    }

    // Remove existing PHOTO element if present
    const existingPhoto = vCardElement.getChild("PHOTO")
    if (existingPhoto) {
      vCardElement.remove(existingPhoto)
    }

    // Add the new PHOTO element
    vCardElement.append(photoElement)

    // Send the updated vCard
    await iqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
        },
        vCardElement,
      ),
    )
  },

  removeVCardPhoto: async (client: Client): Promise<void> => {
    const { iqCaller } = client

    // First fetch the current vCard
    const result = await iqCaller.request(
      xml(
        "iq",
        {
          type: "get",
          to: client.jid?.bare().toString(),
        },
        xml("vCard", "vcard-temp"),
      ),
    )

    // Get the current vCard element
    const vCardElement = result.getChild("vCard", "vcard-temp")

    // If there's no vCard or no PHOTO, nothing to do
    if (!vCardElement) {
      return
    }

    // Remove existing PHOTO element if present
    const existingPhoto = vCardElement.getChild("PHOTO")
    if (existingPhoto) {
      vCardElement.remove(existingPhoto)

      // Send the updated vCard
      await iqCaller.request(
        xml(
          "iq",
          {
            from: client.jid,
            type: "set",
          },
          vCardElement,
        ),
      )
    }
  },
}

export default XmppVCardApi
